import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { usePost } from "../../API";
import { ChatSessionContext } from "../ChatSessionContext";
import { captureException } from "@sentry/react";
import { ApplicationStateContext } from "../ApplicationStateContext";
import { ChatMessageItem } from "@aidkitorg/types/lib/convoai";
import { ChatContextType } from "./ChatContributor";
import { ChatConversationContext, ChatPhase } from "./ConversationContext";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { AIConfigKey, MessageTemplateRef } from "@aidkitorg/types/lib/survey";
import { useAICoach } from "../../AICoach/AICoachContext";

export function useOpenAITextChat({ template, aiConfigKey }: { template?: MessageTemplateRef, aiConfigKey?: AIConfigKey } = {}): ChatContextType {
  const { application, applicantUid, includeTrace, onTrace } = useAICoach();
  const { dispatch, latestMessage } = useContext(ChatConversationContext);
  const { state: appState } = useContext(ApplicationStateContext);
  const { conversationId, loaded } = useContext(ChatSessionContext);
  const chat = usePost('/ai/chat/message');
  const [lastQueryAnswered, setLastQueryAnswered] = useState<number>(latestMessage?.index || -1 /* ensures we reply to 0th index user messages */);
  const traceGenerationRef = useRef(0);

  useEffect(() => {

    let phase: ChatPhase = 'unknown';
    if (loaded) {
      if (conversationId) {
        phase = 'listening';
      }
    } else {
      phase = 'initializing';
    }

    dispatch({ type: 'update_phase', phase });
  }, [loaded, conversationId]);

  const askAssistant = useCallback(async (update: ChatMessageItem) => {
    let traceGeneration = traceGenerationRef.current;
    try {
      dispatch({ type: 'update_phase', phase: 'thinking' });
      if (includeTrace) {
        traceGeneration = traceGenerationRef.current + 1;
        traceGenerationRef.current = traceGeneration;
        onTrace?.(null);
      }
      const response = await chat({
        conversationId: conversationId!,
        application,
        appState,
        newMessages: [update],
        template,
        aiConfigKey,
        ...(applicantUid ? { applicantUid } : {}),
        ...(includeTrace ? { includeTrace: true } : {})
      });

      if (response.trace && traceGeneration === traceGenerationRef.current) {
        onTrace?.(response.trace);
      }

      for (const part of response.items) {
        dispatch(part);
      }
    } catch (e: any) {
      dispatch({
        type: 'error',
        error: {
          type: 'request_error',
          message: e.message
        }
      });
      captureException(e, {
        ...e,
        conversationId
      });
    } finally {
      dispatch({ type: 'update_phase', phase: 'listening' });
    }
  }, [conversationId, appState, chat, template, application, applicantUid, aiConfigKey, includeTrace, onTrace]);

  useAsyncEffect(async () => {

    // indicates that the conversation has reset in some way.
    // resetting lastQueryAnswered so that we can take action on the new messages
    if (!latestMessage) {
      setLastQueryAnswered(-1);
      if (includeTrace) {
        traceGenerationRef.current += 1;
        onTrace?.(null);
      }
      return;
    }

    const { message, index } = latestMessage;

    if (message.type === 'message' && message.role === 'user') {
      if (index > lastQueryAnswered) {
        setLastQueryAnswered(index);
        await askAssistant(message);
      }
    }

  }, [latestMessage, askAssistant, includeTrace, onTrace]);

  return {
    active: true,
    ready: true,
    enabled: true,
    setEnabled() {},
    sendMessage: dispatch,
  }
}
